import React, { useEffect, useState, useMemo } from "react";
import { Card, Row, Col, Spin, Tabs, Select, InputNumber, Empty } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getCompanyNielsenAction } from "../../actions/company.actions";
import Investments from "../../components/Investments/Investments";
import Localize from "../../components/Localized/Localize.comp";
import { getFromLocalStorage } from "../../libs/helpers/localstorage";
import BrandAdvertisersTable from "../../components/BrandAdvertisers/BrandAdvertisersTable";
import Label from "../../components/Label/Label";
import GatedTableEmptyState from "../../design-library/GatedTableEmptyState/GatedTableEmptyState.component";

// Mirrors the full set of Nielsen categories the real feature reports, so
// the blurred preview conveys how much breakdown is actually included —
// not just "there's a chart". Values are fictitious.
const nielsenYear = (radio, newspapers, magazines, outdoor, transit) => ({
  TV: 0,
  "TV SATELLITARI": 0,
  "TV FREE": 0,
  "TV PAY": 0,
  RADIO: radio,
  NEWSPAPERS: newspapers,
  "FREE/PAY PRESS": 0,
  MAGAZINES: magazines,
  OUTDOOR: outdoor,
  "OUT OF HOME TV": 0,
  INTERNET: 0,
  TRANSIT: transit,
  CINEMA: 0,
  "SMART TV/CONSOLE": 0,
  "DESKTOP/TABLET": 0,
  SMARTPHONE: 0,
  "GRAND TOTAL": radio + newspapers + magazines + outdoor + transit,
});

const MOCK_NIELSEN_HISTORY = {
  2024: nielsenYear(107, 494, 123, 239, 254),
  2023: nielsenYear(98, 460, 115, 210, 230),
  2022: nielsenYear(90, 420, 105, 195, 205),
  2021: nielsenYear(82, 380, 95, 175, 180),
  2020: nielsenYear(75, 340, 85, 155, 160),
  2019: nielsenYear(68, 305, 78, 140, 145),
};

const CompanyInvestments = (props) => {
  const {
    companyId,
    selectedYear,
    hideTabs,
    mode,
    nielsenData,
    hasAccess = true,
  } = props;

  const nielsenFromRedux = useSelector((state) => state.company.nielsen);
  const nielsen = nielsenData || nielsenFromRedux;
  const dispatch = useDispatch();
  const tenant = getFromLocalStorage("lefacWebTenant").data;
  const [activeTab, setActiveTab] = useState(mode || "traditional");
  const [digitalFilters, setDigitalFilters] = useState({
    companyIds: [companyId],
    page: 0,
    pageSize: 20,
    reach: selectedYear ? [{ year: selectedYear }] : [],
  });

  const hasTraditionalData = hasAccess
    ? nielsen?.data?.history && Object.keys(nielsen.data.history).length > 0
    : true;

  useEffect(() => {
    if (!nielsenData && hasAccess) {
      dispatch(getCompanyNielsenAction({ id: companyId }));
    }
  }, [companyId, !!nielsenData, hasAccess]);

  useEffect(() => {
    if (selectedYear) {
      setDigitalFilters((prev) => ({
        ...prev,
        reach: [{ year: selectedYear }],
      }));
    }
  }, [selectedYear]);

  const renderTraditionalInvestments = () => (
    <>
      {tenant === "spain" && (
        <Row style={{ marginBottom: 16 }}>
          <Col span="24">
            <p style={{ fontSize: "0.9rem", margin: 0 }}>
              {Localize("PAGES.REGISTRY.SECTIONS.SOURCE")}:{" "}
              <a href="https://www.infoadex.es/" target="_blank">
                <img
                  src={require("../../assets/images/infoadex_logo.png")}
                  alt="InfoAdex"
                  style={{ width: 80, verticalAlign: "middle", marginLeft: 4 }}
                />
              </a>
            </p>
          </Col>
        </Row>
      )}
      {!hasAccess ? (
        <>
          <GatedTableEmptyState
            message={Localize(
              "PAGES.COMPANIES.DETAIL.SECTIONS.TRADITIONAL_INVESTMENTS_GATED_MESSAGE",
            )}
          />
          <div
            aria-hidden
            style={{
              filter: "blur(6px)",
              pointerEvents: "none",
              userSelect: "none",
            }}
          >
            <Investments
              data={MOCK_NIELSEN_HISTORY}
              selectedYear={selectedYear}
              hideYearPicker={!!selectedYear}
            />
          </div>
        </>
      ) : hasTraditionalData ? (
        <Investments
          data={nielsen.data.history}
          selectedYear={selectedYear}
          hideYearPicker={!!selectedYear}
        />
      ) : (
        <Empty
          description={Localize(
            "PAGES.COMPANIES.DETAIL.SECTIONS.TRADITIONAL_INVESTMENTS_EMPTY",
          )}
        />
      )}
    </>
  );

  const renderDigitalInvestments = () => (
    <>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Label
            className="filter-color"
            value={Localize("BRAND_ADVERTISERS.PLATFORM")}
          />
          <Select
            mode="multiple"
            allowClear={true}
            style={{ width: "100%" }}
            value={digitalFilters.advertiserType}
            onChange={(value) =>
              setDigitalFilters({
                ...digitalFilters,
                advertiserType: value,
                page: 0,
              })
            }
            showSearch
            options={[
              {
                key: "META",
                value: "META",
                label: Localize("BRAND_ADVERTISERS.PLATFORM_META"),
              },
              {
                key: "GOOGLE",
                value: "GOOGLE",
                label: Localize("BRAND_ADVERTISERS.PLATFORM_GOOGLE"),
              },
            ]}
          />
        </Col>
        {!selectedYear && (
          <Col span={8}>
            <Label className="filter-color" value={Localize("COMMON.YEAR")} />
            <InputNumber
              style={{ width: "100%" }}
              min={2000}
              max={new Date().getFullYear()}
              value={digitalFilters.reach?.[0]?.year}
              onChange={(value) =>
                setDigitalFilters({
                  ...digitalFilters,
                  reach: value ? [{ year: value }] : [],
                  page: 0,
                })
              }
            />
          </Col>
        )}
      </Row>
      <BrandAdvertisersTable
        filters={digitalFilters}
        onFiltersChange={setDigitalFilters}
        hasAccess={hasAccess}
      />
    </>
  );

  const tabItems = useMemo(() => {
    return [
      {
        key: "traditional",
        label: Localize(
          "PAGES.COMPANIES.DETAIL.SECTIONS.TRADITIONAL_INVESTMENTS",
        ),
        children: renderTraditionalInvestments(),
      },
      {
        key: "digital",
        label: Localize("PAGES.COMPANIES.DETAIL.SECTIONS.DIGITAL_INVESTMENTS"),
        children: renderDigitalInvestments(),
      },
    ];
  }, [nielsen, tenant, digitalFilters, selectedYear, hasAccess]);

  const content = hideTabs ? (
    activeTab === "traditional" ? (
      renderTraditionalInvestments()
    ) : (
      renderDigitalInvestments()
    )
  ) : (
    <Tabs
      activeKey={activeTab}
      onChange={setActiveTab}
      className="investments__subtabs"
      items={tabItems}
    />
  );

  return (
    <>
      {nielsen.loading ? (
        <div className="text-center mt-5">
          <Spin className="mt-5" />
        </div>
      ) : (
        <Card
          title={
            <Row>
              <Col span={24}>
                <h2 className="m-0">
                  <b>
                    {Localize("PAGES.COMPANIES.DETAIL.SECTIONS.INVESTMENTS")}
                  </b>
                </h2>
              </Col>
            </Row>
          }
          bordered={false}
          className="companyPage__body-card companyPage__body-card--custom"
        >
          {content}
        </Card>
      )}
    </>
  );
};

export default CompanyInvestments;
